import { useMemo } from 'react';
import {
  formatDate,
  formatDateTime,
  formatDay,
  formatTime,
  toLocalInputValue,
  type IsoDateTime,
} from '@fleet/shared';
import { useAppConfig } from '@/app/appConfig';

/**
 * Time, formatted in the client's zone.
 *
 * One hook so no screen reaches for `new Date()` on an API value. Kathmandu is
 * UTC+05:45, and a 45 minute offset punishes every shortcut.
 *
 * **The returned functions are memoised, and that is load-bearing.** They used
 * to be rebuilt on every render, which made them useless as effect
 * dependencies — and several screens do exactly that, to fill a form from the
 * server once. With fresh identities each render those effects re-ran forever,
 * resetting fields as fast as somebody could type into them: the payment modal
 * could not be changed at all, and editing a booking wiped every keystroke.
 *
 * Keyed on the timezone, which is the only thing they close over.
 */
export function useTime(): {
  formatDay: (value: IsoDateTime) => string;
  formatDate: (value: IsoDateTime) => string;
  formatTime: (value: IsoDateTime) => string;
  formatDateTime: (value: IsoDateTime) => string;
  toInputValue: (value: IsoDateTime) => string;
} {
  const { locale } = useAppConfig();
  const timezone = locale.timezone;

  return useMemo(() => {
    const format = { timezone };
    return {
      formatDay: (value: IsoDateTime) => formatDay(value, format),
      formatDate: (value: IsoDateTime) => formatDate(value, format),
      formatTime: (value: IsoDateTime) => formatTime(value, format),
      formatDateTime: (value: IsoDateTime) => formatDateTime(value, format),
      toInputValue: (value: IsoDateTime) => toLocalInputValue(value, format),
    };
  }, [timezone]);
}

/**
 * A `datetime-local` value turned back into an instant.
 *
 * The browser gives us wall-clock text with no zone. It is interpreted in the
 * machine's zone, which for a counter in Kathmandu is the right one — and this
 * is the only place that assumption is made.
 */
export function fromInputValue(value: string): string {
  return new Date(value).toISOString();
}

/**
 * What a new booking starts out as: today, midnight to ten at night.
 *
 * The client's ordinary hire is a day of the car, handed over in the morning
 * and back that evening — so the form opens on the shape of the common case
 * and staff adjust the edges, rather than adjusting both every time.
 *
 * It used to default to "in an hour" and "the same time tomorrow", which came
 * from a one-day minimum that no longer exists.
 */
const DEFAULT_OUT_HOUR = 0;
const DEFAULT_BACK_HOUR = 22;

export function suggestedStart(): string {
  const day = new Date();
  day.setHours(DEFAULT_OUT_HOUR, 0, 0, 0);
  return localInput(day);
}

export function suggestedEnd(): string {
  const day = new Date();
  day.setHours(DEFAULT_BACK_HOUR, 0, 0, 0);
  return localInput(day);
}

function localInput(date: Date): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
